import {queryGraphQL, mutateGraphQL} from 'common/util/query';
import {nodeWithArgs, toMutation, toQuery, node, enumeration} from 'common/util/queryHelper';
import lodash from 'lodash';
import { replace } from 'react-router-redux';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';

export const ACTIONS = {
    ADMIN_LOAD_DESCRIPTORS: 'ADMIN_LOAD_DESCRIPTORS',
    ADMIN_CREATE_DESCRIPTOR: 'ADMIN_CREATE_DESCRIPTOR',
    ADMIN_DELETE_DESCRIPTOR: 'ADMIN_DELETE_DESCRIPTOR',
    ADMIN_UPDATE_DESCRIPTOR: 'ADMIN_UPDATE_DESCRIPTOR',
    ADMIN_LOAD_DATATYPES: 'ADMIN_LOAD_DATATYPES'
};


// loadDescriptors is only used in Complion Admin although it currently sits inside of Institutional Admin.
// It should be moved to the appropriate place along with any related code that uses it.
export function loadDescriptors(institutionId) {
    return dispatch => {

        const descriptorsNode = node('ComplionAdmin', [
            nodeWithArgs('descriptors', {
                institutionId
            }, [
                'id',
                'name',
                'systemName',
                'displayName',
                'description',
                'dataType',
                'isActive',
                'label',
                'instructions',
                node('dataset', [
                    'id',
                    'dataType',
                    'values',
                ]),
            ])
        ]);

        const dataTypesNode = node('descriptorDataTypes');

        const query = toQuery([descriptorsNode, dataTypesNode]);

        return queryGraphQL(query).then(results => {
            if (results.errors) {
                dispatch(addApplicationMessage('error', 'Failed to load descriptors for institution.'));
            } else {
                dispatch({
                    type: ACTIONS.ADMIN_LOAD_DESCRIPTORS,
                    payload: {
                        descriptors: results.data.ComplionAdmin.descriptors || [],
                        descriptorDataTypes: results.data.descriptorDataTypes || [],
                        institutionId: institutionId
                    }
                });
            }

        });
    };
}

export function deleteDescriptor(descriptor, institutionId) {
    return dispatch => {

        const mutation = toMutation(
            nodeWithArgs('deleteDescriptor', {
                id: descriptor.id
            })
        );

        descriptor.institutionId = institutionId;
        return mutateGraphQL(mutation).then(results => {
            if (results.errors) {
                dispatch(addApplicationMessage('error', 'Failed to delete the chosen descriptor.'));
            } else {
                dispatch({
                    type: ACTIONS.ADMIN_DELETE_DESCRIPTOR,
                    payload: {
                        descriptor: descriptor,
                        institutionId: institutionId
                    }
                });
            }
        });
    };
}

export function updateDescriptor(institutionId, descriptor) {
    return (dispatch, getState) => {
        descriptor = {
            ...descriptor,
            institutionId: institutionId,
            dataType: enumeration(descriptor.dataType),
            datasetDataType: lodash.isEmpty(descriptor.datasetDataType) ? null : enumeration(descriptor.datasetDataType),
        };

        const mutation = toMutation(
            nodeWithArgs('updateDescriptor', {
                descriptor: descriptor
            }, [
                'id',
                'name',
                'displayName',
                'dataType',
                'isActive',
                'label',
                'instructions',
                node('dataset', [
                    'id',
                    'dataType',
                    'values',
                ]),
            ])
        );
        let shouldRedirect = true;
        return mutateGraphQL(mutation).then(results => {
            if (results.errors) {
                shouldRedirect = false;
                if (results.formattedErrors) {
                    dispatch(addApplicationMessage('error', lodash.head(results.formattedErrors)));
                } else {
                    dispatch(addApplicationMessage('error', 'Failed to update the chosen descriptor.'));
                }
            } else {
                dispatch({
                    type: ACTIONS.ADMIN_UPDATE_DESCRIPTOR,
                    payload: {
                        descriptor: results.data.updateDescriptor,
                        institutionId: institutionId
                    }
                });
            }
        }).then(() => {
            if (!shouldRedirect) {
                return;
            }
            const path = lodash.get(getState(), ['routing', 'locationBeforeTransitions', 'pathname']);
            if (path) {
                const pathPieces = path.split('/');
                pathPieces.pop();
                dispatch(replace(pathPieces.join('/')));
            }
        });
    };
}

export function createDescriptor(institutionId, descriptor) {
    return (dispatch, getState) => {
        if (!descriptor) {
            dispatch({
                type: ACTIONS.ADMIN_CREATE_DESCRIPTOR,
                payload: {
                    descriptor: null,
                    institutionId: institutionId
                }
            });
            return;
        }

        descriptor.institutionId = institutionId;
        descriptor = {
            ...descriptor,
            datasetDataType: lodash.isEmpty(descriptor.datasetDataType) ? null : enumeration(descriptor.datasetDataType),
            dataType: enumeration(descriptor.dataType),
            isActive: descriptor.isActive || false
        };
        const mutation = toMutation(
            nodeWithArgs('addDescriptor', {
                descriptor: descriptor
            }, [
                'id',
                'name',
                'displayName',
                'dataType',
                'isActive',
                'label',
                'instructions',
            ])
        );

        let shouldRedirect = true;
        return mutateGraphQL(mutation).then(results => {
            if (results.errors) {
                shouldRedirect = false;
                if (results.formattedErrors) {
                    dispatch(addApplicationMessage('error', lodash.head(results.formattedErrors)));
                } else {
                    dispatch(addApplicationMessage('error', 'Failed to update the chosen descriptor.'));
                }
            } else {
                dispatch({
                    type: ACTIONS.ADMIN_CREATE_DESCRIPTOR,
                    payload: {
                        descriptor: results.data.addDescriptor,
                        institutionId: institutionId
                    }
                });
            }
        }).then(() => {
            if (!shouldRedirect) {
                return;
            }
            const path = lodash.get(getState(), ['routing', 'locationBeforeTransitions', 'pathname']);
            if (path) {
                const pathPieces = path.split('/');
                pathPieces.pop();
                dispatch(replace(pathPieces.join('/')));
            }
        });
    };
}

export const SET_DATASET_VALUES = 'SET_DATASET_VALUES';
export function addToDataset(institutionId, descriptorId, datasetId, value) {
    return dispatch => {
        const mutation = toMutation(
            nodeWithArgs('addToDataset', {
                id: datasetId,
                value,
            }, [
                'id',
                'values',
            ])
        );

        return mutateGraphQL(mutation).then(results => {
            if (results.errors) {
                dispatch(addApplicationMessage('error', 'Failed to add value to the data set.'));
            } else {
                dispatch({
                    type: SET_DATASET_VALUES,
                    payload: {
                        ...results.data.addToDataset,
                        institutionId,
                        descriptorId,
                    }
                });
            }

        });
    };
}

export function removeFromDataset(institutionId, descriptorId, datasetId, index) {
    return dispatch => {
        const mutation = toMutation(
            nodeWithArgs('removeFromDataset', {
                id: datasetId,
                index,
            }, [
                'id',
                'values',
            ])
        );

        return mutateGraphQL(mutation).then(results => {
            if (results.errors) {
                dispatch(addApplicationMessage('error', 'Failed to remove the value from the dataset.'));
            } else {
                dispatch({
                    type: SET_DATASET_VALUES,
                    payload: {
                        ...results.data.removeFromDataset,
                        institutionId,
                        descriptorId,
                    }
                });
            }
        });
    };
}
